/* eslint-disable */
import { useState, useRef } from "react";
import { COLOR, FONT } from "../utils/theme";
import { Icon } from "../utils/icons";
import DayContextMenu from "./DayContextMenu";
import useWindowWidth from "../utils/useWindowWidth";

// Extract base city (before "→" for transit days like "Oslo → Fjords")
function getBaseCity(city) {
  if (!city) return "";
  if (city.includes("→")) return city.split("→")[0].trim();
  return city;
}

// Determine which city block a day belongs to (day-trip heuristic)
function getDayGroupCity(day, index, allDays) {
  const baseCity = getBaseCity(day.city);
  // Only fold day trips (city contains →) into neighbor block
  if (day.city && day.city.includes("\u2192")) {
    const prevCity = index > 0 ? getBaseCity(allDays[index - 1].city) : null;
    const nextCity = index < allDays.length - 1 ? getBaseCity(allDays[index + 1].city) : null;
    if (prevCity && nextCity && prevCity === nextCity) {
      return prevCity;
    }
  }
  return baseCity;
}

// Group visible days into contiguous runs by group city
function groupByCity(days) {
  const groups = [];
  let current = null;
  days.forEach((d, i) => {
    const groupCity = getDayGroupCity(d, i, days);
    if (!current || current.city !== groupCity) {
      current = { city: groupCity, days: [d] };
      groups.push(current);
    } else {
      current.days.push(d);
    }
  });
  return groups;
}

// Map transitMode to ICON_MAP key
const TRANSIT_ICON = {
  plane: "transport",
  train: "train",
  bus: "bus",
  ferry: "ferry",
  drive: "drive",
};

export default function DayPicker({ days, selDay, goDay, pickerRef, pillRefs, onAddDay, activityCounts, onMoveDay, onDeleteDay }) {
  const { isDesktop } = useWindowWidth();
  const visible = days.filter(d => !d.hidden);
  const blocks = groupByCity(visible);

  const [contextMenu, setContextMenu] = useState(null);
  const longPressTimer = useRef(null);
  const longPressTriggered = useRef(false);

  // Build a flat index counter so pillRefs align across blocks
  let pillIndex = 0;

  // Pre-compute which visible index is the last in its block and what transit icon to show
  const blockBoundaries = new Set();
  const transitIcons = {};
  let flatIdx = 0;
  blocks.forEach((block, bi) => {
    const lastInBlock = flatIdx + block.days.length - 1;
    if (bi < blocks.length - 1) {
      blockBoundaries.add(lastInBlock);
      // Use the first day of the NEXT block's transitMode (transit into next city)
      const nextBlockFirstDay = blocks[bi + 1].days[0];
      transitIcons[lastInBlock] = TRANSIT_ICON[nextBlockFirstDay?.transitMode] || "forward";
    }
    flatIdx += block.days.length;
  });

  return (
    <div
      data-testid="day-picker"
      ref={pickerRef}
      style={{
        background: COLOR.cream, borderBottom: `1px solid ${COLOR.borderWarm}`,
        flexShrink: 0, overflowX: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "none",
      }}
    >
      <div style={{ display: "flex", padding: "8px 12px", gap: 0, minWidth: "max-content", alignItems: "flex-start" }}>
        {blocks.map((block, bi) => {
          const blockPills = block.days.map(d => {
            const i = pillIndex++;
            const isTravel = blockBoundaries.has(i);
            const transitIcon = transitIcons[i];
            return (
              <button
                key={d.id || i}
                data-testid={`day-pill-${d.id || i}`}
                ref={el => pillRefs.current[i] = el}
                onClick={() => {
                  if (longPressTriggered.current) { longPressTriggered.current = false; return; }
                  goDay(i);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setContextMenu({ day: d, position: { x: e.clientX, y: e.clientY } });
                }}
                onTouchStart={(e) => {
                  longPressTriggered.current = false;
                  const touch = e.touches[0];
                  longPressTimer.current = setTimeout(() => {
                    longPressTriggered.current = true;
                    setContextMenu({ day: d, position: { x: touch.clientX, y: touch.clientY } });
                  }, 500);
                }}
                onTouchEnd={() => clearTimeout(longPressTimer.current)}
                onTouchMove={() => clearTimeout(longPressTimer.current)}
                style={{
                  position: "relative",
                  display: "flex", flexDirection: "column", alignItems: "center",
                  padding: "6px 10px",
                  background: selDay === i ? COLOR.mauveDark : COLOR.buttonSecondary,
                  border: d.locked
                    ? `1.5px solid ${COLOR.lockedGreen}`
                    : selDay === i ? `1.5px solid ${COLOR.mauveDark}` : `1.5px solid ${COLOR.borderWarm}`,
                  borderRadius: 12, cursor: "pointer", flexShrink: 0,
                  minWidth: 58, minHeight: 58, justifyContent: "center",
                  transition: "all 0.15s", WebkitTapHighlightColor: "transparent",
                }}
              >
                <span style={{ fontSize: 10, fontWeight: 600, color: selDay === i ? COLOR.white : COLOR.inkMid, fontFamily: FONT.mono, marginBottom: 1 }}>
                  Day {d.order}
                </span>
                <span style={{ fontSize: 10, fontWeight: 400, color: selDay === i ? COLOR.white : COLOR.inkMid, fontFamily: FONT.mono, whiteSpace: "nowrap", marginBottom: 2 }}>
                  {d.computedDate || `Day ${d.order}`}
                </span>
                {(() => {
                  const count = activityCounts?.[d.id] || 0;
                  const displayCount = Math.min(count, 12);
                  return (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 3, maxWidth: 60, justifyContent: "center" }}>
                      {Array.from({ length: displayCount }).map((_, j) => (
                        <div key={j} style={{ width: 4, height: 4, borderRadius: "50%", background: selDay === i ? "rgba(255,255,255,0.7)" : `${COLOR.mauve}44` }} />
                      ))}
                      {count > 12 && (
                        <span style={{ fontSize: 7, lineHeight: 1, color: selDay === i ? "rgba(255,255,255,0.7)" : COLOR.inkMid }}>+</span>
                      )}
                    </div>
                  );
                })()}
                {isTravel && transitIcon && (
                  <span style={{ position: "absolute", top: 3, right: 3, pointerEvents: "none" }}>
                    <Icon name={transitIcon} size={10} color={selDay === i ? COLOR.white : COLOR.inkMid} />
                  </span>
                )}
                {d.locked && (
                  <span style={{ position: "absolute", bottom: 4, right: 5, pointerEvents: "none" }}><Icon name="confirm" size={10} color={COLOR.lockedGreen} strokeWidth={2.5} /></span>
                )}
              </button>
            );
          });

          return (
            <div
              key={bi}
              style={{
                background: COLOR.warmSurface,
                borderRadius: 10,
                padding: "4px 8px",
                marginRight: 8,
                display: "flex", flexDirection: "column",
                flexShrink: 0,
              }}
            >
              <span data-testid={`city-block-${block.city}`} style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.inkMid, marginBottom: 4, paddingLeft: 4, ...(!isDesktop ? { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "calc(100vw - 32px)", display: "block" } : {}) }}>
                {block.city}
              </span>
              <div style={{ display: "flex", gap: 6 }}>
                {blockPills}
              </div>
            </div>
          );
        })}
        {onAddDay && (
          <button
            onClick={onAddDay}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              minWidth: 58, minHeight: 58, padding: "7px 12px",
              background: "transparent", border: `1.5px dashed ${COLOR.mauve}66`,
              borderRadius: 12, color: COLOR.mauve, fontSize: 22,
              cursor: "pointer", flexShrink: 0,
              WebkitTapHighlightColor: "transparent",
              alignSelf: "flex-end",
            }}
          >
            +
          </button>
        )}
      </div>
      {contextMenu && (
        <DayContextMenu
          day={contextMenu.day}
          days={visible}
          position={contextMenu.position}
          onMoveLeft={() => onMoveDay?.(contextMenu.day.id, "left")}
          onMoveRight={() => onMoveDay?.(contextMenu.day.id, "right")}
          onAddDayBefore={() => onAddDay?.(contextMenu.day.order)}
          onAddDayAfter={() => onAddDay?.(contextMenu.day.order + 1)}
          onDeleteDay={() => onDeleteDay?.(contextMenu.day.id)}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
}
